import React, {CSSProperties, PropsWithChildren, useState} from 'react';
import styles from "./layout/Layout.module.scss";
import axios, {AxiosError} from "axios";
import {Button, Loading, useDisappearingFeedback} from "fearless-common-ui";
import Alert from "react-bootstrap/Alert";
import {PencilSquare} from "react-bootstrap-icons";
import {User} from "fearless-common";
import {CloseButton, Modal} from 'react-bootstrap';

type SubsetUser = Pick<User, 'profile_picture' | 'id' | 'first_name' | 'last_name' | 'email'>;

/**
 * View component for the profile picture.
 */
export default function ProfilePicture({ user, size = undefined, className = '', onSuccess, canEdit }: {
    user: SubsetUser;
    size: CSSProperties['width'],
    className?: string;
    onSuccess?: () => void;
    canEdit?: boolean;
}) {
    const url = user?.profile_picture?.url;
    const canBeEdit = Boolean(onSuccess && canEdit);

    return <div className={`${canBeEdit ? 'position-relative cursor-pointer' : ''}`}>{
        url
            ? (
                <span
                    className={[styles.UserImage, className].join(' ')}
                    style={{backgroundImage: `url(${url})`, paddingTop: size, width: size}}
                >
                    &nbsp;
                </span>
            )
            : (
                <span
                    className={[styles.UserImage, className].join(' ')}
                    style={{paddingTop: size, width: size}}
                >
                <span className={styles.Initials}>
                    {typeof user?.first_name === 'string' && user?.first_name ? user?.first_name[0] : user.email[0]}
                </span>
            </span>
            )}
        {canBeEdit && user?.id && (
            <ProfilePictureDropzone
                user={user}
                onSuccess={onSuccess}
                className="position-absolute end-0 bottom-0"
            >
                <PencilSquare className="mb-1" />
            </ProfilePictureDropzone>
        )}
    </div>;
}

const profilePictureId = 'pff-picture';

function ProfilePictureDropzone({ user, onSuccess, children, className }: PropsWithChildren<{
    /** The user of whom you are editing the profile picture */
    user: SubsetUser;
    className?: string;
    onSuccess?: () => void;
}>) {
    const { id: userId } = user;
    const fileId = user?.profile_picture?.id;
    const { feedback: message, setFeedback: setMessage} = useDisappearingFeedback();
    const { feedback: error, setFeedback: setError} = useDisappearingFeedback();
    const [isUploading, setUploading] = useState(false);
    const [show, setShow] = useState(false);
    // State containing the newly uploaded file which has not yet been uploaded to the API backend
    const [newFileList, setNewFile] = useState<FileList | null>(null);

    const hideModal = () => {
        setShow(false);
    }
    const deletePicture = () => {
        if (fileId) {
            return axios.delete(`/files/${fileId}`)
              .then(({ data }) => {
                  if (data.message) setMessage(data.message);
                  if (typeof onSuccess === 'function') {
                      onSuccess();
                  }
              })
              .catch((e: AxiosError<{ message: string }>) => {
                  console.log(e.response?.data);
                  setError(e.response?.data?.message || e.message);
              });
        }
    }

    const submitNewFileToBackend = () => {
        if (newFileList) {
            setUploading(true);
            setError('');
            setMessage('');

            const fd = new FormData();
            fd.append('file', newFileList[0]);
            fd.append('name', newFileList[0].name);
            fd.append('user_id', `${userId}`);
            return axios.post('/files', fd, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            })
              .then(() => {
                  if (typeof onSuccess === 'function') {
                      // To refresh to upper components
                      onSuccess();
                  }
                  hideModal();
                  setNewFile(null);
              })
              .catch((e) => {
                  setError(e.response.data?.message || 'Something went wrong...')
              })
              .finally(() => {
                  setUploading(false)
              })
        } else {
            return Promise.reject(new Error('Please upload an image first'));
        }
    }

    return (
        <>
            <span className={`text-center ${className}`}>
                {isUploading && <Loading />}
                <div className="p-0 d-inline-flex align-items-stretch" style={{ height: 42, width: 42 }}>
                    <Button
                      className="flex-grow-1"
                      variant="secondary"
                      type="button"
                      onClick={() => setShow(true)}
                    >
                        {children}
                    </Button>
                </div>
                {show && (
                  <Modal show onHide={hideModal} contentClassName="border-neutral-400">
                      <Modal.Body className="pe-4 pt-4 ps-4 pb-0 bg-neutral-100 rounded-3">
                          <CloseButton className="position-absolute end-0 me-3 top-0 mt-3" onClick={hideModal} />
                          <div className="mb-3 text-center">
                              <ProfilePicture
                                  // @ts-ignore
                                  user={newFileList ? {
                                      ...user,
                                      profile_picture: { ...user.profile_picture, url: URL.createObjectURL(newFileList[0]) },
                                  } : user}
                                  size={220}
                                  className="shadow"
                              />
                          </div>
                          <div className="d-flex gap-2">
                              <Button
                                  type="button"
                                  variant="outline-light"
                                  className="px-4 bg-white border-neutral-500"
                              >
                                  <label htmlFor={profilePictureId}>
                                      {fileId ? 'Change' : 'Upload'}
                                  </label>
                              </Button>
                              {(fileId || newFileList) && (
                                  <Button
                                      type="button"
                                      onClick={newFileList ? () => setNewFile(null) : deletePicture}
                                      variant="outline-light"
                                      className="px-4 bg-white border-neutral-500"
                                  >
                                      Delete
                                  </Button>
                              )}
                              <div className="flex-grow-1">&nbsp;</div>
                              <Button
                                type="button"
                                onClick={newFileList ? submitNewFileToBackend : () => {
                                    setError('Please upload a file first');
                                }}
                                variant="secondary"
                                className="px-4"
                                isDoing={isUploading}
                              >
                                  Save
                              </Button>
                              {!newFileList && (
                                  <Button
                                    type="button"
                                    onClick={hideModal}
                                    variant="outline-light"
                                    className="px-4 bg-white border-neutral-500"
                                  >
                                      Close
                                  </Button>
                              )}
                          </div>
                          <input
                            name="files.0"
                            type="file"
                            id={profilePictureId}
                            value={undefined}
                            disabled={isUploading}
                            style={{ visibility: "hidden" }}
                            onChange={(event) => {
                                // Prevent double upload
                                if (!isUploading && event.currentTarget.files) {
                                    setNewFile(event.currentTarget.files);
                                }
                            }}
                            placeholder="select new file"
                          />
                          {error && <Alert variant="danger">{error}</Alert>}
                          {message && <Alert variant="success">{message}</Alert>}
                      </Modal.Body>
                  </Modal>
                )}
            </span>
        </>
    )
}
